import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import SkudPassEditModal from '@/components/SkudPassEditModal';

/**
 * Универсальный блок «Пропуска СКУД» для карточки человека (ученика,
 * сотрудника, родителя).
 *
 * Подтягивает пропуска по `skudPersonId` и рисует их таблицей. Поддерживает:
 *   • создать новый пропуск (кнопка «+ Пропуск»),
 *   • редактировать (срок / группа / комментарий),
 *   • быстро заблокировать/разблокировать,
 *   • удалить (через подтверждение).
 *
 * Все операции уезжают в Orion через outbound-воркер (pending_sync=1),
 * после ответа от API список перезагружается.
 *
 * Право на действия — `skud.passes/write` и `skud.passes/delete`.
 */
export default function SkudPassesBlock({ skudPersonId }) {
  const { permissions } = useAuth();
  const isSuper = !!permissions.isSuperAdmin;
  const can = (key, action) => isSuper || !!(permissions.can?.[key]?.[action]);
  const canWrite  = can('skud.passes', 'write');
  const canDelete = can('skud.passes', 'delete');

  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPass, setEditPass]     = useState(null);

  const reload = () =>
    api.get(`/api/skud/persons/${skudPersonId}/passes`)
      .then((res) => setRows(res.rows || []))
      .catch(() => setRows([]));

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [skudPersonId]);

  const fmt = (s) => {
    if (!s) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
  };

  const toggleBlocked = async (p) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await api.put(`/api/skud/passes/${p.skudId}`, { isBlocked: !p.isBlocked });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось изменить статус');
    } finally { setBusy(false); }
  };

  const onDelete = async (p) => {
    if (busy) return;
    if (!confirm(`Удалить пропуск ${p.code}? После подтверждения изменение уйдёт в Orion.`)) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/api/skud/passes/${p.skudId}`);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось удалить');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 6 }}>
        {canWrite && (
          <button type="button" className="btn btn--primary btn--sm"
                  onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={12} /> Пропуск
          </button>
        )}
      </div>

      {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5, marginBottom: 6 }}>{error}</div>}

      {rows === null ? (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Загружаем пропуска…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Пропусков нет.</div>
      ) : (
        <table className="table" style={{ fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 90 }}>СКУД #</th>
              <th>Код карты</th>
              <th style={{ width: 110 }}>С</th>
              <th style={{ width: 110 }}>По</th>
              <th style={{ width: 130 }}>Статус</th>
              {(canWrite || canDelete) && <th style={{ width: 140, textAlign: 'right' }}>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.skudId}>
                <td className="mono">{r.skudId > 0 ? r.skudId : <span className="muted">локальный</span>}</td>
                <td className="mono">{r.code}</td>
                <td className="mono">{fmt(r.startDate)}</td>
                <td className="mono">{fmt(r.endDate)}</td>
                <td>
                  {r.isBlocked ? (
                    <span style={{ color: 'var(--red-fg)' }}>✗ заблокирован</span>
                  ) : (
                    <span style={{ color: 'var(--green-700)' }}>✓ активен</span>
                  )}
                  {r.pendingSync && (
                    <span className="pay-st pay-st--mute" style={{ marginLeft: 6, fontSize: 10 }}>
                      в очереди
                    </span>
                  )}
                </td>
                {(canWrite || canDelete) && (
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canWrite && (
                      <button type="button" className="btn btn--sm" disabled={busy}
                              onClick={() => setEditPass(r)} style={{ marginRight: 4 }}
                              title="Изменить">
                        <Icon name="edit" size={11} />
                      </button>
                    )}
                    {canWrite && (
                      <button type="button" className="btn btn--sm" disabled={busy}
                              onClick={() => toggleBlocked(r)} style={{ marginRight: 4 }}
                              title={r.isBlocked ? 'Разблокировать' : 'Заблокировать'}>
                        {r.isBlocked ? '🔓' : '🔒'}
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="btn btn--sm" disabled={busy}
                              onClick={() => onDelete(r)} title="Удалить"
                              style={{ color: 'var(--red-fg, #dc2626)' }}>
                        <Icon name="trash" size={11} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createOpen && (
        <SkudPassEditModal
          mode="create"
          skudPersonId={skudPersonId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); reload(); }}
        />
      )}
      {editPass && (
        <SkudPassEditModal
          mode="edit"
          pass={editPass}
          onClose={() => setEditPass(null)}
          onSaved={() => { setEditPass(null); reload(); }}
        />
      )}
    </>
  );
}
